/**
 * Image compression utility using HTML5 Canvas
 * Compresses an image file if it exceeds targetMaxBytes, reducing dimensions and quality.
 * Returns the original file if already under targetMaxBytes or if not an image.
 */
export async function compressImageIfNeeded(file, targetMaxBytes = 2 * 1024 * 1024) {
  // If already within limit or not a compressible image type, return as is
  if (!file || file.size <= targetMaxBytes) {
    return file;
  }

  // Only compress standard raster image formats (skip SVG, GIF animations, etc.)
  const compressibleTypes = ['image/jpeg', 'image/png', 'image/webp'];
  if (!compressibleTypes.includes(file.type)) {
    return file;
  }

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;

      img.onload = async () => {
        let width = img.width;
        let height = img.height;

        // Max dimension bounds (e.g. 1920x1920 max for avatar is more than enough)
        const maxDimension = 1920;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        // Determine output MIME type: JPEG or WebP (better compression than PNG)
        const outputMime = file.type === 'image/webp' ? 'image/webp' : 'image/jpeg';
        const fileExt = outputMime === 'image/webp' ? '.webp' : '.jpg';
        const originalNameBase = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
        const newFileName = `${originalNameBase}${fileExt}`;

        // Attempt multiple quality levels down to 0.5
        const qualitySteps = [0.85, 0.75, 0.65, 0.5];
        let bestBlob = null;

        for (const quality of qualitySteps) {
          const blob = await new Promise((res) => canvas.toBlob(res, outputMime, quality));
          if (blob) {
            bestBlob = blob;
            if (blob.size <= targetMaxBytes) {
              break;
            }
          }
        }

        // If still over targetMaxBytes, try resizing canvas dimensions further (70%)
        if (bestBlob && bestBlob.size > targetMaxBytes) {
          const scale = 0.7;
          const scaledCanvas = document.createElement('canvas');
          scaledCanvas.width = Math.max(1, Math.round(width * scale));
          scaledCanvas.height = Math.max(1, Math.round(height * scale));
          const scaledCtx = scaledCanvas.getContext('2d');
          scaledCtx.fillStyle = '#FFFFFF';
          scaledCtx.fillRect(0, 0, scaledCanvas.width, scaledCanvas.height);
          scaledCtx.drawImage(canvas, 0, 0, scaledCanvas.width, scaledCanvas.height);

          for (const quality of [0.7, 0.5]) {
            const blob = await new Promise((res) => scaledCanvas.toBlob(res, outputMime, quality));
            if (blob) {
              bestBlob = blob;
              if (blob.size <= targetMaxBytes) {
                break;
              }
            }
          }
        }

        if (bestBlob) {
          const compressedFile = new File([bestBlob], newFileName, {
            type: outputMime,
            lastModified: Date.now(),
          });
          resolve(compressedFile);
        } else {
          // Fallback to original
          resolve(file);
        }
      };

      img.onerror = () => resolve(file);
    };

    reader.onerror = () => resolve(file);
  });
}
